import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, downloadFile, upload } from "../api/client";
import type { Essential, EssentialType, Project } from "../api/types";
import { Icon } from "./icons";
import { SectionHead } from "./SectionHead";
import { toast } from "./toast";

// The project's Essentials — everything the PM needs for a book in one place:
// Product ID, manuscript, table of contents, the review folder link + password,
// and the Box folder link, plus any custom extras. Assets download in a click,
// links open in a new tab in a click, passwords copy in a click.

function copy(text: string, label: string) {
  navigator.clipboard?.writeText(text).then(
    () => toast(`${label} copied`, "success"),
    () => toast("Couldn't copy", "error"),
  );
}
function openUrl(val: string) {
  const u = /^https?:\/\//.test(val) ? val : "https://" + val.replace(/^\/+/, "");
  window.open(u, "_blank", "noopener");
}

/** Which canonical PATCH field a bound default writes to (assets use upload). */
const PATCH_FIELD: Record<string, string> = {
  product_id: "product_id",
  production_partner: "production_partner",
  review_link: "link_review",
  review_pw: "link_review_pw",
  box_folder: "link_folder",
};

/** The editable string value of an essential, by type. */
function valueOf(e: Essential): string {
  if (e.type === "text") return e.value.text ?? "";
  if (e.type === "asset") return e.value.name ?? "";
  if (e.type === "link") return e.value.url ?? "";
  return e.value.pw ?? "";
}
function withValue(e: Essential, s: string): Essential {
  const key: keyof Essential["value"] =
    e.type === "text" ? "text" : e.type === "asset" ? "name" : e.type === "link" ? "url" : "pw";
  return { ...e, value: { [key]: s } };
}

export function ProjectEssentials({ project, onChanged }: { project: Project; onChanged: () => void }) {
  const qc = useQueryClient();
  const items = project.essentials ?? [];
  const customs = items.filter((e) => !e.bound);
  const [editing, setEditing] = useState<string | null>(null);
  const [reveal, setReveal] = useState<Record<string, boolean>>({});
  const manuscriptInput = useRef<HTMLInputElement | null>(null);
  // Production Partner names already used on the PM's other projects, offered as
  // suggestions so the same studio is never typed two ways.
  const { data: partners } = useQuery({
    queryKey: ["production-partners"],
    queryFn: () => api<{ names: string[] }>("/api/projects/production-partners").then((r) => r.names),
  });

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api(`/api/projects/${project.id}/essentials`, { method: "PATCH", body: JSON.stringify(patch) }),
    onSuccess: () => { setEditing(null); onChanged(); qc.invalidateQueries({ queryKey: ["project", project.id] }); qc.invalidateQueries({ queryKey: ["production-partners"] }); },
    onError: (e: Error) => toast(e.message || "Couldn't save", "error"),
  });

  // Persist an edit to one essential. Bound defaults write their canonical
  // field; custom items rebuild the whole custom list.
  function commit(e: Essential, next: string) {
    if (e.bound) {
      const field = PATCH_FIELD[e.id];
      // Never a silent no-op: a field this page doesn't know was added after it
      // loaded (the server sends the list), and the box would just sit open.
      if (!field) { toast("This page is out of date. Reload it to save this.", "error"); return; }
      save.mutate({ [field]: next.trim() });
      return;
    }
    const nextCustoms = customs.map((c) => (c.id === e.id ? stripped(withValue(e, next.trim())) : stripped(c)));
    save.mutate({ custom: nextCustoms });
  }
  function commitLabel(e: Essential, label: string) {
    const nextCustoms = customs.map((c) => (c.id === e.id ? { ...stripped(c), label: label.trim() || "Untitled" } : stripped(c)));
    save.mutate({ custom: nextCustoms });
  }
  function removeCustom(id: string) {
    save.mutate({ custom: customs.filter((c) => c.id !== id).map(stripped) });
  }
  function addCustom(type: EssentialType) {
    const id = `c_${Date.now().toString(36)}`;
    const label = type === "link" ? "New link" : type === "pass" ? "New password" : "New note";
    save.mutate({ custom: [...customs.map(stripped), { id, label, type, value: {} }] });
    setEditing(id);
  }

  const uploadManuscript = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData(); form.append("file", file);
      return upload<Project>(`/api/projects/${project.id}/manuscript`, form);
    },
    onSuccess: () => { toast("Manuscript added.", "success"); onChanged(); qc.invalidateQueries({ queryKey: ["project", project.id] }); },
    onError: (e: Error) => toast(e.message || "Couldn't upload", "error"),
  });

  // The manuscript download link for proof listeners: its own public link,
  // separate from the Pickups Package's, switched on and off right here.
  const msShare = project.manuscript_share;
  const msShareUrl = msShare?.enabled && msShare.token ? `${window.location.origin}/share/manuscript/${msShare.token}` : null;
  const toggleMsShare = useMutation({
    mutationFn: (enable: boolean) =>
      api<{ enabled: boolean; token: string | null }>(`/api/projects/${project.id}/manuscript/share`, { method: enable ? "POST" : "DELETE" }),
    onSuccess: (r) => {
      if (r.enabled && r.token) {
        // Copy it straight away, the usual next step; the switch worked either way.
        navigator.clipboard?.writeText(`${window.location.origin}/share/manuscript/${r.token}`).then(
          () => toast("Download link on, and copied", "success"),
          () => toast("Download link on", "success"),
        );
      } else toast("Download link turned off.", "info");
      onChanged(); qc.invalidateQueries({ queryKey: ["project", project.id] });
    },
    onError: (e: Error) => toast(e.message || "Couldn't update the download link", "error"),
  });

  return (
    <>
      {/* The heading sits ABOVE the card, like Assets and Services — one section
          pattern across the hub rather than three. */}
      <SectionHead title="Essentials" sub="Everything this project needs, in one place." />
      <div className="card pe-card">
      <ul className="pe-list">
        {items.map((e) => [
          (
          <li key={e.id} className={`pe-row${e.present ? "" : " missing"}`}>
            <span className="pe-label">
              {editing === e.id && !e.bound ? (
                <input
                  className="pe-input pe-label-input"
                  defaultValue={e.label}
                  onKeyDown={(ev) => { if (ev.key === "Enter") commitLabel(e, (ev.target as HTMLInputElement).value); if (ev.key === "Escape") setEditing(null); }}
                  onBlur={(ev) => commitLabel(e, ev.target.value)}
                />
              ) : (
                e.label
              )}
            </span>

            <span className="pe-value">
              {editing === e.id ? (
                <EditField e={e} suggestions={e.id === "production_partner" ? partners : undefined}
                  onSave={(v) => commit(e, v)} onCancel={() => setEditing(null)} />
              ) : (
                <Display e={e} reveal={!!reveal[e.id]} />
              )}
            </span>

            <span className="pe-actions">
              {/* --- assets: download / view / replace / add --- */}
              {e.id === "manuscript" && e.present && (
                <>
                  <IconBtn icon="download" title="Download the manuscript"
                    onClick={() => downloadFile(`/api/projects/${project.id}/manuscript.pdf?download=1`)} />
                  <Link className="pe-btn" to={`/projects/${project.id}/manuscript`} title="Review how it was read"><Icon name="file-text" size={14} /></Link>
                  <IconBtn icon="upload" title="Replace the manuscript" onClick={() => manuscriptInput.current?.click()} />
                </>
              )}
              {e.id === "manuscript" && !e.present && (
                <button className="pe-btn add" onClick={() => manuscriptInput.current?.click()}><Icon name="upload" size={13} /> Upload</button>
              )}
              {e.id === "toc" && e.present && (
                <>
                  <IconBtn icon="download" title="Download the Table of Contents"
                    onClick={() => downloadFile(`/api/modules/toc/projects/${project.id}/toc/file`)} />
                  <Link className="pe-btn" to={`/projects/${project.id}/toc`} title="Open the Table of Contents check"><Icon name="external" size={14} /></Link>
                </>
              )}
              {e.id === "toc" && !e.present && (
                <Link className="pe-btn add" to={`/projects/${project.id}/toc`}><Icon name="plus" size={13} /> Add</Link>
              )}

              {/* --- links: open / copy / edit --- */}
              {e.type === "link" && e.present && (
                <>
                  <IconBtn icon="external" title="Open in a new tab" onClick={() => openUrl(valueOf(e))} />
                  <IconBtn icon="copy" title="Copy the link" onClick={() => copy(valueOf(e), e.label)} />
                </>
              )}
              {/* --- passwords: reveal / copy --- */}
              {e.type === "pass" && e.present && (
                <>
                  <button className="pe-btn" title={reveal[e.id] ? "Hide" : "Show"} onClick={() => setReveal((r) => ({ ...r, [e.id]: !r[e.id] }))}>{reveal[e.id] ? "Hide" : "Show"}</button>
                  <IconBtn icon="copy" title="Copy the password" onClick={() => copy(valueOf(e), e.label)} />
                </>
              )}
              {/* --- text: copy --- */}
              {e.type === "text" && e.present && (
                <IconBtn icon="copy" title={`Copy the ${e.label}`} onClick={() => copy(valueOf(e), e.label)} />
              )}

              {/* --- edit (everything but the two file assets) --- */}
              {e.type !== "asset" && editing !== e.id && (
                <IconBtn icon="edit" title={e.present ? "Edit" : "Add"} onClick={() => setEditing(e.id)} />
              )}
              {!e.bound && (
                <IconBtn icon="trash" title="Remove this essential" onClick={() => removeCustom(e.id)} />
              )}
            </span>
          </li>
          ),
          e.id === "manuscript" && e.present && (
            <li key="manuscript-share" className="pe-row pe-subrow">
              <span className="pe-label">Download link</span>
              <span className="pe-value">
                {msShareUrl ? (
                  <button className="pe-linktext" title="Open the download page" onClick={() => openUrl(msShareUrl)}>{msShareUrl}</button>
                ) : (
                  <span className="pe-empty">Off. Turn on to give proof listeners their own copy.</span>
                )}
              </span>
              <span className="pe-actions">
                {msShareUrl && <IconBtn icon="copy" title="Copy the download link" onClick={() => copy(msShareUrl, "Download link")} />}
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!msShare?.enabled}
                  className={`share-toggle${msShare?.enabled ? " on" : ""}`}
                  title={msShare?.enabled
                    ? "Proof listeners can download the manuscript from this link. Click to turn it off"
                    : "Make a download link for the manuscript (separate from the Pickups Package link)"}
                  disabled={toggleMsShare.isPending}
                  onClick={() => toggleMsShare.mutate(!msShare?.enabled)}
                >
                  <span className="share-toggle-track"><span className="share-toggle-knob" /></span>
                  <span className="share-toggle-label">{msShare?.enabled ? "On" : "Off"}</span>
                </button>
              </span>
            </li>
          ),
        ])}
      </ul>

      <div className="pe-add">
        <span className="muted">Add:</span>
        <button className="pe-btn" onClick={() => addCustom("link")}><Icon name="plus" size={13} /> Link</button>
        <button className="pe-btn" onClick={() => addCustom("pass")}><Icon name="plus" size={13} /> Password</button>
        <button className="pe-btn" onClick={() => addCustom("text")}><Icon name="plus" size={13} /> Note</button>
      </div>

      <input ref={manuscriptInput} type="file" accept="application/pdf,.pdf,.docx,.doc" hidden
        onChange={(ev) => { const f = ev.target.files?.[0]; ev.target.value = ""; if (f) uploadManuscript.mutate(f); }} />
      </div>
    </>
  );
}

// A bound default's custom fields (label/notes) aren't stored; only real custom
// items round-trip. Keep just what the API persists.
function stripped(e: Essential) {
  return { id: e.id, label: e.label, type: e.type, value: e.value, notes: e.notes ?? "", shared: !!e.shared };
}

function IconBtn({ icon, title, onClick }: { icon: string; title: string; onClick: () => void }) {
  return <button className="pe-btn" title={title} onClick={onClick}><Icon name={icon} size={14} /></button>;
}

function Display({ e, reveal }: { e: Essential; reveal: boolean }) {
  if (!e.present) return <span className="pe-empty">Not set</span>;
  if (e.type === "pass") return <code className="pe-mono">{reveal ? valueOf(e) : "••••••••"}</code>;
  if (e.type === "text") return <code className="pe-mono">{valueOf(e)}</code>;
  if (e.type === "asset") return <span className="pe-file"><Icon name="document" size={14} /> {valueOf(e)}</span>;
  // link — show it, click to open
  return <button className="pe-linktext" title="Open in a new tab" onClick={() => openUrl(valueOf(e))}>{valueOf(e)}</button>;
}

function EditField({ e, suggestions, onSave, onCancel }: { e: Essential; suggestions?: string[]; onSave: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(valueOf(e));
  return (
    <span className="pe-edit">
      <input
        className="pe-input"
        autoFocus
        type={e.type === "pass" ? "text" : "text"}
        list={suggestions ? `pe-suggest-${e.id}` : undefined}
        value={v}
        placeholder={e.type === "link" ? "https://…" : e.type === "pass" ? "password" : "value"}
        onChange={(ev) => setV(ev.target.value)}
        onKeyDown={(ev) => { if (ev.key === "Enter") onSave(v); if (ev.key === "Escape") onCancel(); }}
      />
      {suggestions && (
        <datalist id={`pe-suggest-${e.id}`}>
          {suggestions.map((n) => <option key={n} value={n} />)}
        </datalist>
      )}
      <button className="pe-btn primary" title="Save" onClick={() => onSave(v)}><Icon name="check" size={14} /></button>
      <button className="pe-btn" title="Cancel" onClick={onCancel}><Icon name="x" size={14} /></button>
    </span>
  );
}
